// src/pages/appointment/AppointmentList.js
// Página para gestionar citas con vista diaria

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button, Spinner, Modal, Form, Card, Alert } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import DailySchedule from '../../components/DailySchedule';
import AppointmentForm from './AppointmentForm';
import InformedConsentForm from './InformedConsentForm';
import { useAuth } from '../../context/AuthContext';
import { getAppointmentsByDate, updateAppointment, deleteAppointment, createAppointment, getSaleByAppointment, getGroomingItems } from '../../services/appointmentApi';
import { getAppointmentDetails, addAppointmentDetail, deleteAppointmentDetail } from '../../services/appointmentDetailApi';
import { generateAppointmentReceiptPdf } from '../../utils/appointmentReceiptPdfGenerator';
import { useBranding } from '../../context/BrandingContext';
import { getInformedConsentByAppointment } from '../../services/informedConsentApi';
import AlertModal from '../../components/AlertModal';
import useAlertModal from '../../hooks/useAlertModal';

const AppointmentList = () => {
  const { user, canSeeAllAppointments, idEmployee } = useAuth();
  const branding = useBranding();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedDate, setSelectedDate] = useState(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) return dateParam;
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  const [appointments, setAppointments] = useState([]);
  /** Todas las citas del día (para calcular horarios disponibles en el modal) */
  const [allAppointmentsForDay, setAllAppointmentsForDay] = useState([]);
  /** Solo para vista groomer: slots ocupados por otros (sin detalle) */
  const [occupiedSlots, setOccupiedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showAlert, showAlertModal, alertMessage, alertType, hideAlert, getErrorMessage } = useAlertModal();
  
  // Estados para modales
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedAppointmentForConsent, setSelectedAppointmentForConsent] = useState(null);
  const [consentByAppointmentId, setConsentByAppointmentId] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [slotTimeForCreate, setSlotTimeForCreate] = useState(null);

  // Cargar citas del día según rol: admin/gerente ven todas; groomer solo las suyas + slots ocupados por otros
  const loadAppointments = useCallback(async () => {
    try {
      setLoading(true);
      setOccupiedSlots([]);

      if (canSeeAllAppointments) {
        const { ok, status, data } = await getAppointmentsByDate(selectedDate);
        if (ok && (status === 200 || data?.code === 200)) {
          const list = data.data || data.message || [];
          const arr = Array.isArray(list) ? list : list?.appointments || [];
          setAppointments(arr);
          setAllAppointmentsForDay(arr);
        } else {
          const msg = getErrorMessage({ data, status }) || 'Error cargando citas';
          showAlert(msg, 'error');
        }
        return;
      }

      if (idEmployee != null) {
        const [mineRes, allRes] = await Promise.all([
          getAppointmentsByDate(selectedDate, idEmployee),
          getAppointmentsByDate(selectedDate)
        ]);
        const mineList = mineRes.ok && (mineRes.status === 200 || mineRes.data?.code === 200)
          ? (mineRes.data.data || mineRes.data.message || [])
          : [];
        const allList = allRes.ok && (allRes.status === 200 || allRes.data?.code === 200)
          ? (allRes.data.data || allRes.data.message || [])
          : [];
        const myAppointments = Array.isArray(mineList) ? mineList : mineList?.appointments || [];
        const allAppointments = Array.isArray(allList) ? allList : allList?.appointments || [];
        setAppointments(myAppointments);
        setAllAppointmentsForDay(allAppointments || []);
        const myIds = new Set((myAppointments || []).map((a) => a.id_appointment));
        const occupied = new Set();
        (allAppointments || []).forEach((apt) => {
          if (apt.id_appointment && !myIds.has(apt.id_appointment) && apt.start_at) {
            const d = new Date(apt.start_at);
            const slot = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
            occupied.add(slot);
          }
        });
        setOccupiedSlots(Array.from(occupied));
      } else {
        const { ok, status, data } = await getAppointmentsByDate(selectedDate);
        if (ok && (status === 200 || data?.code === 200)) {
          const list = data.data || data.message || [];
          const arr = Array.isArray(list) ? list : list?.appointments || [];
          setAppointments(arr);
          setAllAppointmentsForDay(arr);
        } else {
          const msg = getErrorMessage({ data, status }) || 'Error cargando citas';
          showAlert(msg, 'error');
        }
      }
    } catch (error) {
      console.error('Error cargando citas:', error);
      showAlert('Error inesperado al cargar citas', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, canSeeAllAppointments, idEmployee]);

  // Cargar citas cuando cambie la fecha seleccionada
  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Cargar consentimientos por cita cuando cambien las citas
  useEffect(() => {
    if (!appointments?.length) {
      setConsentByAppointmentId({});
      return;
    }
    const loadConsents = async () => {
      const map = {};
      await Promise.all(
        appointments.map(async (apt) => {
          const id = apt.id_appointment;
          if (!id) return;
          const { ok, data } = await getInformedConsentByAppointment(id);
          if (!ok) return;
          const raw = data?.data ?? data?.message;
          const consent = Array.isArray(raw) ? raw[0] : raw;
          if (consent?.url_pdf_firebase) {
            map[id] = { url_pdf_firebase: consent.url_pdf_firebase };
          }
        })
      );
      setConsentByAppointmentId(map);
    };
    loadConsents();
  }, [appointments]);

  // Manejar cambio de fecha
  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    // Actualizar la URL con la nueva fecha
    setSearchParams({ date: newDate });
  };

  // Sincronizar la fecha desde los parámetros de la URL al montar
  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (dateParam && dateParam !== selectedDate) {
      setSelectedDate(dateParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Abrir modal de agendar cita desde slot (hora precargada)
  const handleSlotClick = (slotTime) => {
    setSlotTimeForCreate(slotTime || '09:00');
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setSlotTimeForCreate(null);
  };

  const handleCreateAppointment = async (appointmentData) => {
    try {
      setModalLoading(true);
      const { ok, status, data } = await createAppointment(appointmentData);
      if (ok && (status === 200 || status === 201 || data?.code === 200 || data?.code === 201)) {
        showAlert(data?.message || 'Cita agendada correctamente.', 'success');
        handleCloseCreateModal();
        loadAppointments();
      } else {
        const msg = getErrorMessage({ data, status }) || 'Error al agendar la cita.';
        showAlert(msg, 'error');
      }
    } catch (error) {
      console.error('Error al agendar cita:', error);
      showAlert('Error inesperado al agendar la cita.', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // Manejar edición de cita
  const handleEditAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    console.log(appointment);
    setShowEditModal(true);
  };

  // Cerrar modal de edición
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedAppointment(null);
  };

  // Manejar eliminación de cita
  const handleDeleteAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    setShowDeleteModal(true);
  };

  // Cerrar modal de eliminación
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedAppointment(null);
  };

  // Abrir modal de consentimiento informado
  const handleConsentClick = (appointment) => {
    setSelectedAppointmentForConsent(appointment);
    setShowConsentModal(true);
  };

  // Cerrar modal de consentimiento
  const handleCloseConsentModal = () => {
    setShowConsentModal(false);
    setSelectedAppointmentForConsent(null);
  };

  // Descargar recibo de pago (solo si la cita tiene venta asociada)
  const handleReceiptClick = async (appointment) => {
    if (!appointment?.id_appointment) return;
    try {
      const [saleRes, detailsRes, itemsRes] = await Promise.all([
        getSaleByAppointment(appointment.id_appointment),
        getAppointmentDetails(appointment.id_appointment),
        getGroomingItems()
      ]);
      const saleRaw = saleRes?.data?.data ?? saleRes?.data?.message ?? saleRes?.data;
      const saleData = (saleRes?.ok && saleRaw && typeof saleRaw === 'object' && saleRaw.id_sale)
        ? saleRaw
        : null;
      if (!saleData?.id_sale) {
        showAlert('Esta cita no tiene venta asociada. Solo se puede generar recibo para citas pagadas.', 'error');
        return;
      }
      const services = detailsRes?.ok && detailsRes?.data?.code === 200
        ? (detailsRes.data.data || detailsRes.data.message || [])
        : [];
      const items = itemsRes?.ok && itemsRes?.data?.code === 200
        ? (itemsRes.data.data || itemsRes.data.message || [])
        : [];
      const itemsMap = {};
      (Array.isArray(items) ? items : []).forEach((it) => {
        if (it.id_item) itemsMap[it.id_item] = it;
      });
      const doc = generateAppointmentReceiptPdf(
        appointment,
        saleData,
        Array.isArray(services) ? services : [],
        itemsMap,
        { companyName: branding.companyName,
          legalID: branding.legalID,
          address: branding.address
        }
      );
      doc.save(`recibo-cita-${appointment.id_appointment}-${Date.now()}.pdf`);
      showAlert('Recibo descargado correctamente.', 'success');
    } catch (error) {
      console.error('Error generando recibo:', error);
      showAlert('Error al generar el recibo.', 'error');
    }
  };

  // Consentimiento guardado correctamente
  const handleConsentSuccess = (responseData) => {
    showAlert('Consentimiento informado guardado correctamente.', 'success');
    const aptId = selectedAppointmentForConsent?.id_appointment;
    const urlPdf = responseData?.url_pdf_firebase || responseData?.data?.url_pdf_firebase;
    if (aptId && urlPdf) {
      setConsentByAppointmentId((prev) => ({
        ...prev,
        [aptId]: { url_pdf_firebase: urlPdf }
      }));
    }
    handleCloseConsentModal();
    loadAppointments();
  };

  // Confirmar eliminación
  const handleConfirmDelete = async () => {
    if (!selectedAppointment) return;

    try {
      setModalLoading(true);
      
      const { ok, status, data } = await deleteAppointment(selectedAppointment.id_appointment);
      
      if (ok && (status === 200 || data?.code === 200)) {
        showAlert(data?.message || 'Cita eliminada exitosamente', 'success');
        setShowDeleteModal(false);
        setSelectedAppointment(null);
        // Recargar citas del día
        loadAppointments();
      } else {
        const msg = getErrorMessage({ data, status }) || 'Error eliminando cita';
        showAlert(msg, 'error');
      }
    } catch (error) {
      console.error('Error eliminando cita:', error);
      showAlert('Error inesperado al eliminar cita', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // Manejar actualización de cita (incluye sincronizar carrito de servicios si viene en payload)
  const handleUpdateAppointment = async (appointmentData) => {
    try {
      setModalLoading(true);
      const idAppointment = selectedAppointment.id_appointment;

      // Sincronizar primero el carrito de servicios para que, al pasar a PAGADA,
      // la venta se cree con el detalle final actualizado.
      if (Array.isArray(appointmentData.items)) {
        const { ok: detailsOk, data: detailsData } = await getAppointmentDetails(idAppointment);
        const existing = detailsOk && detailsData?.code === 200
          ? (detailsData.data || detailsData.message || [])
          : [];
        const list = Array.isArray(existing) ? existing : [];
        for (const row of list) {
          if (row.id_appointment_detail) {
            await deleteAppointmentDetail(row.id_appointment_detail);
          }
        }
        for (const it of appointmentData.items) {
          await addAppointmentDetail({
            id_appointment: idAppointment,
            id_item: it.id_item,
            quantity: it.quantity ?? 1,
            unit_price: it.unit_price ?? 0
          });
        }
      }

      const { ok, status, data } = await updateAppointment(idAppointment, appointmentData);
      if (ok && (status === 200 || data?.code === 200)) {
        const msg = data?.message || 'Cita actualizada exitosamente';
        const idSale = data?.id_sale;
        const finalMsg = idSale != null
          ? `${msg} (Venta #${idSale})`
          : msg;
        showAlert(finalMsg, 'success');
        setShowEditModal(false);
        setSelectedAppointment(null);
        loadAppointments();
      } else {
        const msg = getErrorMessage({ data, status }) || 'Error actualizando cita';
        showAlert(msg, 'error');
      }
    } catch (error) {
      console.error('Error actualizando cita:', error);
      showAlert('Error inesperado al actualizar cita', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // Formatear fecha para mostrar (parsear como fecha local para evitar desfase por UTC)
  const formatSelectedDate = () => {
    if (!selectedDate) return '';
    const [year, month, day] = selectedDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return date.toLocaleDateString('es-ES', options);
  };

  return (
    <>
      <NavContainer />
      <OffCanvasNav />

      <Container fluid className="mt-3">
        <Row>
          <Col>

            {/* Selector de fecha */}
            <Row className="mb-4">
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Seleccionar Fecha</h5>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group>
                      <Form.Label>Fecha a consultar:</Form.Label>
                      <Form.Control
                        type="date"
                        value={selectedDate}
                        onChange={handleDateChange}
                        max={new Date(new Date().getFullYear() + 1, 11, 31).toISOString().split('T')[0]}
                        min={new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0]}
                      />
                    </Form.Group>
                    <div className="mt-2">
                      <small className="text-muted">
                        <strong>Fecha seleccionada:</strong> {formatSelectedDate()}
                      </small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Resumen del Día</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>Total de citas:</strong> {appointments.length}
                      </div>
                      <div>
                        <Button 
                          variant="primary" 
                          size="sm"
                          onClick={() => {
                            setSlotTimeForCreate(null);
                            setShowCreateModal(true);
                          }}
                        >
                          <i className="fas fa-plus me-1"></i>
                          Nueva Cita
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Vista del horario diario */}
            {loading ? (
              <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" size="lg" />
              </div>
            ) : (
              <DailySchedule
                date={selectedDate}
                appointments={appointments}
                occupiedSlots={occupiedSlots}
                onEditAppointment={handleEditAppointment}
                onDeleteAppointment={handleDeleteAppointment}
                onConsentClick={handleConsentClick}
                onReceiptClick={handleReceiptClick}
                onSlotClick={handleSlotClick}
                consentByAppointmentId={consentByAppointmentId}
              />
            )}
          </Col>
        </Row>

        {/* Modal de agendar cita desde slot */}
        <Modal show={showCreateModal} onHide={handleCloseCreateModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              Agendar Cita — {selectedDate}
              {slotTimeForCreate && (
                <span className="text-muted fw-normal ms-2">a las {slotTimeForCreate}</span>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AppointmentForm
              key={`create-${slotTimeForCreate || 'new'}`}
              onSubmit={handleCreateAppointment}
              loading={modalLoading}
              selectedDate={selectedDate}
              defaultTime={slotTimeForCreate}
              appointmentsForDate={allAppointmentsForDay}
              submitText="Agendar Cita"
              onAlert={showAlert}
              defaultEmployeeId={idEmployee}
            />
          </Modal.Body>
        </Modal>

        {/* Modal de edición de cita */}
        <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              Editar Cita - {selectedAppointment?.pet_name || ''}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedAppointment && (
              <AppointmentForm
                onSubmit={handleUpdateAppointment}
                loading={modalLoading}
                initialData={selectedAppointment}
                selectedDate={selectedDate}
                appointmentsForDate={allAppointmentsForDay}
                submitText="Actualizar Cita"
                onAlert={showAlert}
              />
            )}
          </Modal.Body>
        </Modal>

        {/* Modal de consentimiento informado */}
        <Modal show={showConsentModal} onHide={handleCloseConsentModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fas fa-file-signature me-2"></i>
              Consentimiento informado
              {selectedAppointmentForConsent?.pet_name && (
                <span className="text-muted fw-normal ms-2">
                  — {selectedAppointmentForConsent.pet_name}
                </span>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedAppointmentForConsent && (
              <InformedConsentForm
                appointment={selectedAppointmentForConsent}
                onSuccess={handleConsentSuccess}
                onCancel={handleCloseConsentModal}
              />
            )}
          </Modal.Body>
        </Modal>

        {/* Modal de confirmación de eliminación */}
        <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
          <Modal.Header closeButton>
            <Modal.Title>Confirmar Eliminación</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              ¿Estás seguro de que deseas eliminar la cita de{' '}
              <strong>{selectedAppointment?.pet_name || ''}</strong>?
            </p>
            <div className="bg-light p-3 rounded">
              <small>
                <strong>Fecha:</strong> {selectedAppointment?.start_at ? 
                  new Date(selectedAppointment.start_at).toLocaleString('es-ES') : 'N/A'}<br/>
                <strong>Cliente:</strong> {selectedAppointment?.client_name || 'N/A'}<br/>
                <strong>Servicio:</strong> {selectedAppointment?.service_name || 'N/A'}
              </small>
            </div>
            <Alert variant="warning" className="mt-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              Esta acción no se puede deshacer.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseDeleteModal}>
              Cancelar
            </Button>
            <Button 
              variant="danger" 
              onClick={handleConfirmDelete}
              disabled={modalLoading}
            >
              {modalLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Eliminando...
                </>
              ) : (
                <>
                  <i className="fas fa-trash me-2"></i>
                  Eliminar Cita
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
        {/* Modal de Alerta Temporal */}
        <AlertModal
          show={showAlertModal}
          onHide={hideAlert}
          message={alertMessage}
          type={alertType}
        />
      </Container>
    </>
  );
};

export default AppointmentList;